import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Monitor } from 'lucide-react';
import { getScheduleItems, timeToMinutes } from '../firebase/dailyScheduleService';
import { getClasses, getLessons, getUnits } from '../firebase/lessonPlannerService';
import { getAssignments } from '../firebase/assignmentService';
import './DailySummary.css';

// Utility function to convert 24-hour time to 12-hour AM/PM format
const formatTime12Hour = (time24) => {
  const [hours, minutes] = time24.split(':').map(Number);
  const period = hours >= 12 ? 'PM' : 'AM';
  const hours12 = hours % 12 || 12;
  return `${hours12}:${minutes.toString().padStart(2, '0')} ${period}`;
};

function DailySummary() {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const [currentTime, setCurrentTime] = useState(new Date());
  const [scheduleItems, setScheduleItems] = useState([]);
  const [classes, setClasses] = useState([]);
  const [lessons, setLessons] = useState([]);
  const [units, setUnits] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [currentItem, setCurrentItem] = useState(null);
  const [activeTimers, setActiveTimers] = useState([]);
  const [todaysLessons, setTodaysLessons] = useState([]);
  const [weeklyLessons, setWeeklyLessons] = useState([]);
  const [loading, setLoading] = useState(true);

  // Manual Timer/Stopwatch State
  const [timerMode, setTimerMode] = useState('stopwatch'); // 'stopwatch' or 'countdown'
  const [timerRunning, setTimerRunning] = useState(false);
  const [elapsedSeconds, setElapsedSeconds] = useState(0);
  const [countdownMinutes, setCountdownMinutes] = useState(5);
  const [showTimerPanel, setShowTimerPanel] = useState(false);
  const [showClock, setShowClock] = useState(true);
  const [timerPosition, setTimerPosition] = useState({ x: window.innerWidth - 370, y: 80 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });

  // Update current time every second
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  // Manual timer update
  useEffect(() => {
    if (!timerRunning) return;

    const interval = setInterval(() => {
      setElapsedSeconds(prev => {
        if (timerMode === 'countdown') {
          const newValue = prev - 1;
          if (newValue <= 0) {
            setTimerRunning(false);
            // Play sound or show alert when countdown reaches 0
            return 0;
          }
          return newValue;
        } else {
          return prev + 1;
        }
      });
    }, 1000);

    return () => clearInterval(interval);
  }, [timerRunning, timerMode]);

  // Load data
  useEffect(() => {
    if (!currentUser) {
      navigate('/');
      return;
    }
    loadData();
  }, [currentUser, navigate]);

  const loadData = async () => {
    setLoading(true);
    const [scheduleResult, classesResult, lessonsResult, unitsResult, assignmentsResult] = await Promise.all([
      getScheduleItems(currentUser.uid),
      getClasses(currentUser.uid),
      getLessons(currentUser.uid),
      getUnits(currentUser.uid),
      getAssignments(currentUser.uid)
    ]);

    if (scheduleResult.success) {
      setScheduleItems(scheduleResult.items);
    }
    if (classesResult.success) {
      setClasses(classesResult.classes);
    }
    if (lessonsResult.success) {
      setLessons(lessonsResult.lessons);
    }
    if (unitsResult.success) {
      setUnits(unitsResult.units);
    }
    if (assignmentsResult.success) {
      setAssignments(assignmentsResult.assignments);
    }
    setLoading(false);
  };

  // Determine current class period and active timers
  useEffect(() => {
    const currentMinutes = currentTime.getHours() * 60 + currentTime.getMinutes();
    const dayOfWeek = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'][currentTime.getDay()];

    // Filter items for today
    const todaysItems = scheduleItems.filter(item =>
      item.daysOfWeek?.includes(dayOfWeek)
    );

    // Find current class period
    const current = todaysItems.find(item => {
      if (item.type !== 'class') return false;
      const startMinutes = timeToMinutes(item.startTime);
      const endMinutes = timeToMinutes(item.endTime);

      // Handle classes that span midnight (e.g., 23:00-00:00)
      let isMatch;
      if (endMinutes <= startMinutes) {
        // Class spans midnight
        isMatch = currentMinutes >= startMinutes || currentMinutes < endMinutes;
      } else {
        // Normal class within same day
        isMatch = currentMinutes >= startMinutes && currentMinutes < endMinutes;
      }

      return isMatch;
    });

    // Only log when current item changes or when no item is found
    if (!current && todaysItems.some(item => item.type === 'class')) {
      console.group('🔍 DEBUG: No current class found');
      console.log('Current time:', {
        time: currentTime.toLocaleTimeString(),
        dayOfWeek,
        currentMinutes
      });
      console.log('All schedule items:', scheduleItems.map(item => ({
        type: item.type,
        classId: item.classId,
        className: classes.find(c => c.id === item.classId)?.name || 'Unknown',
        startTime: item.startTime,
        endTime: item.endTime,
        daysOfWeek: item.daysOfWeek,
        matchesDay: item.daysOfWeek?.includes(dayOfWeek)
      })));

      console.log('Today\'s class items:');
      todaysItems.filter(item => item.type === 'class').forEach(item => {
        const startMinutes = timeToMinutes(item.startTime);
        const endMinutes = timeToMinutes(item.endTime);
        const spansMidnight = endMinutes <= startMinutes;
        const wouldMatch = spansMidnight ?
          (currentMinutes >= startMinutes || currentMinutes < endMinutes) :
          (currentMinutes >= startMinutes && currentMinutes < endMinutes);

        console.log({
          className: classes.find(c => c.id === item.classId)?.name || item.classId,
          startTime: item.startTime,
          endTime: item.endTime,
          startMinutes,
          endMinutes,
          spansMidnight,
          currentMinutes,
          wouldMatch,
          reason: !wouldMatch ? (spansMidnight ?
            `Need: ${currentMinutes} >= ${startMinutes} OR ${currentMinutes} < ${endMinutes}` :
            `Need: ${currentMinutes} >= ${startMinutes} AND ${currentMinutes} < ${endMinutes}`) : '✅ MATCHED'
        });
      });
      console.groupEnd();
    }

    setCurrentItem(current || null);

    // Find active timers (manual timers and class countdowns)
    const timers = [];

    // Add manual timers (within 5 minutes)
    todaysItems.forEach(item => {
      if (item.type === 'timer') {
        const timerMinutes = timeToMinutes(item.startTime);
        const diff = timerMinutes - currentMinutes;
        if (diff >= 0 && diff <= 5) {
          timers.push(item);
        }
      }
    });

    // Add class countdown timers
    todaysItems.forEach(item => {
      if (item.type === 'class') {
        const startMinutes = timeToMinutes(item.startTime);
        const endMinutes = timeToMinutes(item.endTime);

        // Tardy countdown (before class starts)
        if (item.tardyCountdownEnabled && item.tardyCountdownMinutes) {
          const tardyTime = startMinutes - item.tardyCountdownMinutes;
          const diff = currentMinutes - tardyTime;
          if (diff >= 0 && diff < 1) {
            timers.push({
              id: `${item.id}-tardy`,
              type: 'countdown',
              timerType: 'tardy',
              timerName: `Tardy Warning - ${getClassName(item.classId)}`,
              className: getClassName(item.classId),
              isExpiring: true
            });
          }
        }

        // Bell countdown (before class ends)
        if (item.bellCountdownEnabled && item.bellCountdownMinutes) {
          const bellTime = endMinutes - item.bellCountdownMinutes;
          const diff = currentMinutes - bellTime;
          if (diff >= 0 && diff < 1) {
            timers.push({
              id: `${item.id}-bell`,
              type: 'countdown',
              timerType: 'bell',
              timerName: `Bell Warning - ${getClassName(item.classId)}`,
              className: getClassName(item.classId),
              isExpiring: true
            });
          }
        }
      }
    });

    setActiveTimers(timers);

    // Find today's lesson and weekly lessons for current class
    if (current) {
      const classObj = classes.find(c => c.id === current.classId);
      console.log('Current class:', current, 'Class obj:', classObj);

      if (classObj) {
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const todayStr = today.toISOString().split('T')[0];

        console.log('Looking for lessons on:', todayStr, 'Class unit ID:', classObj.unitId);
        console.log('All lessons:', lessons);

        // Get start and end of current week (Monday to Sunday)
        const dayOfWeek = today.getDay();
        const diff = dayOfWeek === 0 ? -6 : 1 - dayOfWeek; // Adjust for Sunday
        const monday = new Date(today);
        monday.setDate(today.getDate() + diff);
        monday.setHours(0, 0, 0, 0);

        const sunday = new Date(monday);
        sunday.setDate(monday.getDate() + 6);
        sunday.setHours(23, 59, 59, 999);

        console.log('Week range:', monday, 'to', sunday);

        // Find lessons for this class in this week
        const classLessons = lessons.filter(l => {
          const matchesUnit = l.unitIds?.includes(classObj.unitId) || l.unitId === classObj.unitId;
          const lessonDate = l.startDate || l.scheduledDate;

          if (!matchesUnit) {
            console.log('Lesson', l.title, 'does not match unit. Lesson unitId:', l.unitId, 'unitIds:', l.unitIds);
            return false;
          }

          if (!lessonDate) {
            console.log('Lesson', l.title, 'has no date');
            return false;
          }

          const lessonDateObj = parseDateLocal(lessonDate);
          const inRange = lessonDateObj >= monday && lessonDateObj <= sunday;

          console.log('Lesson', l.title, 'date:', lessonDate, 'in range:', inRange);

          return inRange;
        }).sort((a, b) => {
          const dateA = parseDateLocal(a.startDate || a.scheduledDate);
          const dateB = parseDateLocal(b.startDate || b.scheduledDate);
          return dateA - dateB;
        });

        console.log('Weekly lessons found:', classLessons);

        // Find all lessons for today
        const todayLessons = classLessons.filter(l => {
          const lessonDate = l.startDate || l.scheduledDate;
          return lessonDate === todayStr || lessonDate.startsWith(todayStr);
        });

        console.log('Today\'s lessons:', todayLessons);

        setTodaysLessons(todayLessons);
        setWeeklyLessons(classLessons);
      }
    } else {
      setTodaysLessons([]);
      setWeeklyLessons([]);
    }
  }, [currentTime, scheduleItems, classes, lessons]);

  const formatTime = (date) => {
    return date.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      second: '2-digit'
    });
  };

  // Helper function to parse YYYY-MM-DD string as local date (not UTC)
  const parseDateLocal = (dateString) => {
    const [year, month, day] = dateString.split('-').map(Number);
    return new Date(year, month - 1, day);
  };

  const getClassName = (classId) => {
    const classObj = classes.find(c => c.id === classId);
    return classObj?.name || 'Unknown Class';
  };

  const getUnitNames = (lesson) => {
    if (!lesson) return '';

    const unitIds = lesson.unitIds || (lesson.unitId ? [lesson.unitId] : []);
    if (unitIds.length === 0) return '';

    const unitNames = unitIds
      .map(id => {
        const unit = units.find(u => u.id === id);
        return unit?.name;
      })
      .filter(Boolean);

    return unitNames.join(', ');
  };

  const getCurrentAssignments = () => {
    if (!currentItem) return [];

    const classObj = classes.find(c => c.id === currentItem.classId);
    if (!classObj) return [];

    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const todayTime = today.getTime();

    return assignments
      .filter(a => {
        // Filter by class/unit
        if (a.classId !== currentItem.classId && a.unitId !== classObj.unitId) return false;

        // Get due date
        const dueDate = a.dueDate?.toDate ? a.dueDate.toDate() : new Date(a.dueDate);
        if (!dueDate) return false;

        // Include current and upcoming (within next 14 days)
        const dueDateOnly = new Date(dueDate);
        dueDateOnly.setHours(0, 0, 0, 0);
        const diff = (dueDateOnly.getTime() - todayTime) / (1000 * 60 * 60 * 24);

        return diff >= 0 && diff <= 14;
      })
      .sort((a, b) => {
        const dateA = a.dueDate?.toDate ? a.dueDate.toDate() : new Date(a.dueDate);
        const dateB = b.dueDate?.toDate ? b.dueDate.toDate() : new Date(b.dueDate);
        return dateA - dateB;
      })
      .slice(0, 3);
  };

  // Timer control functions
  const startTimer = () => {
    if (timerMode === 'countdown' && elapsedSeconds === 0) {
      setElapsedSeconds(countdownMinutes * 60);
    }
    setTimerRunning(true);
  };

  const stopTimer = () => {
    setTimerRunning(false);
  };

  const resetTimer = () => {
    setTimerRunning(false);
    setElapsedSeconds(0);
  };

  const switchMode = (mode) => {
    setTimerMode(mode);
    resetTimer();
  };

  const formatTimerDisplay = () => {
    const hours = Math.floor(elapsedSeconds / 3600);
    const minutes = Math.floor((elapsedSeconds % 3600) / 60);
    const seconds = elapsedSeconds % 60;

    if (hours > 0) {
      return `${hours}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
    }
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  // Timer dragging handlers
  const handleTimerMouseDown = (e) => {
    if (e.target.closest('.timer-close-btn') || e.target.closest('.timer-controls')) return;
    setIsDragging(true);
    setDragOffset({
      x: e.clientX - timerPosition.x,
      y: e.clientY - timerPosition.y
    });
  };

  const handleTimerMouseMove = (e) => {
    if (!isDragging) return;
    setTimerPosition({
      x: e.clientX - dragOffset.x,
      y: e.clientY - dragOffset.y
    });
  };

  const handleTimerMouseUp = () => {
    setIsDragging(false);
  };

  useEffect(() => {
    if (isDragging) {
      document.addEventListener('mousemove', handleTimerMouseMove);
      document.addEventListener('mouseup', handleTimerMouseUp);
      return () => {
        document.removeEventListener('mousemove', handleTimerMouseMove);
        document.removeEventListener('mouseup', handleTimerMouseUp);
      };
    }
  }, [isDragging, dragOffset]);

  if (loading) {
    return (
      <div className="daily-summary-loading">
        <div className="loading-spinner"></div>
        <p>Loading...</p>
      </div>
    );
  }

  return (
    <div className="daily-summary-view">
      <div className="summary-header">
        <div className="header-left-section">
          <h1>Mr. Willeby's Class</h1>
          {currentItem && (
            <div className="current-class-info">
              <span className="class-name-header">{getClassName(currentItem.classId)}</span>
              {currentItem.period && <span className="period-badge">Period {currentItem.period}</span>}
              <span className="class-time-header">
                {formatTime12Hour(currentItem.startTime)} - {formatTime12Hour(currentItem.endTime)}
              </span>
            </div>
          )}
        </div>

        <div className="header-center-section">
          {showClock && (
            <div className="current-time-large">{formatTime(currentTime)}</div>
          )}
        </div>

        <div className="header-right-section">
          <button
            className="clock-toggle-btn-compact"
            onClick={() => setShowClock(!showClock)}
            title={showClock ? "Hide clock" : "Show clock"}
          >
            {showClock ? '🕐' : '⏰'}
          </button>
          <button className="timer-toggle-btn-compact" onClick={() => setShowTimerPanel(true)}>
            ⏱️
          </button>
          <button onClick={() => navigate('/big-screen-view')} className="back-btn-compact">
            <Monitor size={16} />
            <span>Big Screen</span>
          </button>
          <button onClick={() => navigate(-1)} className="back-btn-compact">
            <ArrowLeft size={16} />
            <span>Back</span>
          </button>
        </div>
      </div>

      {/* Manual Timer/Stopwatch */}
      {showTimerPanel && (
        <div
          className="manual-timer-widget expanded"
          style={{
            left: `${timerPosition.x}px`,
            top: `${timerPosition.y}px`,
            cursor: isDragging ? 'grabbing' : 'grab'
          }}
        >
          <div className="timer-panel" onMouseDown={handleTimerMouseDown}>
            <div className="timer-panel-header">
              <div className="timer-mode-switch">
                <button
                  className={timerMode === 'stopwatch' ? 'active' : ''}
                  onClick={() => switchMode('stopwatch')}
                >
                  Stopwatch
                </button>
                <button
                  className={timerMode === 'countdown' ? 'active' : ''}
                  onClick={() => switchMode('countdown')}
                >
                  Countdown
                </button>
              </div>
              <button className="timer-close-btn" onClick={() => setShowTimerPanel(false)}>
                ✕
              </button>
            </div>

            {timerMode === 'countdown' && !timerRunning && elapsedSeconds === 0 && (
              <div className="countdown-setup">
                <label>Minutes:</label>
                <input
                  type="number"
                  min="1"
                  max="120"
                  value={countdownMinutes}
                  onChange={(e) => setCountdownMinutes(Math.max(1, parseInt(e.target.value) || 1))}
                />
              </div>
            )}

            <div className="timer-display">
              {formatTimerDisplay()}
            </div>

            <div className="timer-controls">
              {!timerRunning ? (
                <button className="timer-btn start-btn" onClick={startTimer}>
                  ▶️ Start
                </button>
              ) : (
                <button className="timer-btn stop-btn" onClick={stopTimer}>
                  ⏸️ Stop
                </button>
              )}
              <button className="timer-btn reset-btn" onClick={resetTimer}>
                🔄 Reset
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Timer Warnings */}
      {activeTimers.length > 0 && (
        <div className="timer-warnings">
          {activeTimers.map(timer => {
            const isCountdown = timer.type === 'countdown';
            let icon = '🔔';
            let message = 'Bell is about to ring!';

            if (timer.timerType === 'seats') {
              icon = '🪑';
              message = 'Get to your seats!';
            } else if (timer.timerType === 'tardy') {
              icon = '⚠️';
              message = 'Class is starting soon - don\'t be late!';
            } else if (timer.timerType === 'bell' && isCountdown) {
              icon = '🔔';
              message = 'Class is ending soon!';
            }

            return (
              <div key={timer.id} className={`timer-warning ${timer.timerType} ${isCountdown ? 'countdown' : ''}`}>
                <div className="timer-icon">
                  {icon}
                </div>
                <div className="timer-message">
                  <h2>{timer.timerName}</h2>
                  <p>{message}</p>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {currentItem ? (
        <div className="current-class-section">
          <div className="summary-grid">
            {/* Today's Lesson */}
            <div className="summary-card lesson-card">
              <h3>📖 Today's Lesson{todaysLessons.length > 1 ? 's' : ''}</h3>
              {todaysLessons.length > 0 ? (
                <>
                  {todaysLessons.map((lesson, idx) => (
                    <div key={lesson.id}>
                      <h4>{lesson.title}</h4>
                      {getUnitNames(lesson) && (
                        <div className="lesson-unit-badge">{getUnitNames(lesson)}</div>
                      )}
                    </div>
                  ))}
                  {todaysLessons.some(l => l.learningObjective) && (
                    <div className="lesson-section">
                      <strong>Learning Objective{todaysLessons.filter(l => l.learningObjective).length > 1 ? 's' : ''}:</strong>
                      {todaysLessons.map((lesson, idx) =>
                        lesson.learningObjective ? (
                          <p key={idx}>{todaysLessons.length > 1 ? `• ` : ''}{lesson.learningObjective}</p>
                        ) : null
                      )}
                    </div>
                  )}
                  {todaysLessons.some(l => l.lessonSummary) && (
                    <div className="lesson-section">
                      <strong>Summar{todaysLessons.filter(l => l.lessonSummary).length > 1 ? 'ies' : 'y'}:</strong>
                      {todaysLessons.map((lesson, idx) =>
                        lesson.lessonSummary ? (
                          <p key={idx}>{todaysLessons.length > 1 ? `• ` : ''}{lesson.lessonSummary}</p>
                        ) : null
                      )}
                    </div>
                  )}
                </>
              ) : (
                <p className="no-data">No lesson scheduled for today</p>
              )}
            </div>

            {/* Right column - Assignments and Resources */}
            <div className="summary-card-column">
              {/* Assignments */}
              <div className="summary-card-compact assignments-card">
                <h3>📝 Assignments</h3>
                {getCurrentAssignments().length > 0 ? (
                  <ul className="assignments-list">
                    {getCurrentAssignments().map((assignment, idx) => {
                      const dueDate = assignment.dueDate?.toDate ? assignment.dueDate.toDate() : new Date(assignment.dueDate);
                      const today = new Date();
                      today.setHours(0, 0, 0, 0);
                      const isToday = dueDate.toDateString() === today.toDateString();
                      const isTomorrow = dueDate.toDateString() === new Date(today.getTime() + 86400000).toDateString();

                      return (
                        <li key={idx} className={isToday ? 'due-today' : ''}>
                          <strong>{assignment.title}</strong>
                          <span className="assignment-due">
                            {isToday ? 'Due Today' : isTomorrow ? 'Due Tomorrow' : dueDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                          </span>
                        </li>
                      );
                    })}
                  </ul>
                ) : (
                  <p className="no-data">No upcoming assignments</p>
                )}
              </div>

              {/* Resources */}
              <div className="summary-card-compact resources-card">
                <h3>🔗 Resources</h3>
                {(() => {
                  const allResources = todaysLessons.flatMap(lesson => lesson.resources || []);
                  return allResources.length > 0 ? (
                    <div className="resources-list">
                      {allResources.map((resource, idx) => {
                        const url = resource.fileUrl || resource.url;
                        return url ? (
                          <a
                            key={idx}
                            href={url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="resource-link"
                          >
                            <span className="resource-icon">📄</span>
                            <span>{resource.description || `Resource ${idx + 1}`}</span>
                          </a>
                        ) : null;
                      })}
                    </div>
                  ) : (
                    <p className="no-data">No resources available</p>
                  );
                })()}
              </div>
            </div>
          </div>

          {/* Weekly Lesson Summary */}
          <div className="weekly-summary">
            <h3>📅 This Week's Lessons</h3>
            <div className="weekly-days-grid">
              {(() => {
                const today = new Date();
                today.setHours(0, 0, 0, 0);

                // Get Monday of current week
                const dayOfWeek = today.getDay();
                const diff = dayOfWeek === 0 ? -6 : 1 - dayOfWeek;
                const monday = new Date(today);
                monday.setDate(today.getDate() + diff);

                // Generate 6 days starting from Monday (Mon-Sat, no Sunday)
                const daysOfWeek = [];
                for (let i = 0; i < 6; i++) {
                  const day = new Date(monday);
                  day.setDate(monday.getDate() + i);
                  daysOfWeek.push(day);
                }

                return daysOfWeek.map((day, idx) => {
                  const dayStr = day.toISOString().split('T')[0];
                  const isToday = dayStr === today.toISOString().split('T')[0];
                  const dayName = day.toLocaleDateString('en-US', { weekday: 'short' });
                  const monthDay = day.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
                  const isSaturday = idx === 5;

                  // Find lessons for this day
                  const dayLessons = weeklyLessons.filter(l => {
                    const lessonDate = l.startDate || l.scheduledDate;
                    return lessonDate === dayStr || lessonDate?.startsWith(dayStr);
                  });

                  // Find assignments for this day
                  const dayAssignments = assignments.filter(a => {
                    if (!a.assignmentDate) return false;
                    const assignDate = a.assignmentDate.toDate
                      ? a.assignmentDate.toDate().toISOString().split('T')[0]
                      : a.assignmentDate;
                    return assignDate === dayStr || assignDate?.startsWith(dayStr);
                  });

                  // For Saturday, check Sunday too for weekend events
                  let sundayLessons = [];
                  let sundayAssignments = [];
                  if (isSaturday) {
                    const sunday = new Date(day);
                    sunday.setDate(day.getDate() + 1);
                    const sundayStr = sunday.toISOString().split('T')[0];
                    sundayLessons = weeklyLessons.filter(l => {
                      const lessonDate = l.startDate || l.scheduledDate;
                      return lessonDate === sundayStr || lessonDate?.startsWith(sundayStr);
                    });
                    sundayAssignments = assignments.filter(a => {
                      if (!a.assignmentDate) return false;
                      const assignDate = a.assignmentDate.toDate
                        ? a.assignmentDate.toDate().toISOString().split('T')[0]
                        : a.assignmentDate;
                      return assignDate === sundayStr || assignDate?.startsWith(sundayStr);
                    });
                  }

                  return (
                    <div key={idx} className={`weekly-day-column ${isToday ? 'today' : ''}`}>
                      <div className="weekly-day-header">
                        <h4>{isSaturday ? 'Weekend' : dayName}</h4>
                        <span className="weekly-date-label">{monthDay}</span>
                      </div>
                      <div className="weekly-day-content">
                        {isSaturday ? (
                          // Weekend view
                          dayLessons.length === 0 && sundayLessons.length === 0 && dayAssignments.length === 0 && sundayAssignments.length === 0 ? (
                            <div className="no-lessons-day">
                              <p>No Events This Weekend</p>
                            </div>
                          ) : (
                            <>
                              {dayLessons.map(lesson => (
                                <div key={lesson.id} className="weekly-lesson-item">
                                  <div className="weekly-lesson-item-title">{lesson.title}</div>
                                  {getUnitNames(lesson) && (
                                    <div className="weekly-lesson-item-unit">{getUnitNames(lesson)}</div>
                                  )}
                                  {lesson.duration && (
                                    <div className="weekly-lesson-item-duration">{lesson.duration} min</div>
                                  )}
                                  {lesson.resources && lesson.resources.length > 0 && (
                                    <div className="weekly-lesson-item-resources">
                                      {lesson.resources.map((resource, ridx) => {
                                        const url = resource.fileUrl || resource.url;
                                        return url ? (
                                          <a
                                            key={ridx}
                                            href={url}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="weekly-resource-link-small"
                                            onClick={(e) => e.stopPropagation()}
                                          >
                                            📄 {resource.description || `Resource ${ridx + 1}`}
                                          </a>
                                        ) : null;
                                      })}
                                    </div>
                                  )}
                                </div>
                              ))}
                              {sundayLessons.map(lesson => (
                                <div key={lesson.id} className="weekly-lesson-item">
                                  <div className="weekly-lesson-item-title">{lesson.title}</div>
                                  {getUnitNames(lesson) && (
                                    <div className="weekly-lesson-item-unit">{getUnitNames(lesson)}</div>
                                  )}
                                  {lesson.duration && (
                                    <div className="weekly-lesson-item-duration">{lesson.duration} min</div>
                                  )}
                                  {lesson.resources && lesson.resources.length > 0 && (
                                    <div className="weekly-lesson-item-resources">
                                      {lesson.resources.map((resource, ridx) => {
                                        const url = resource.fileUrl || resource.url;
                                        return url ? (
                                          <a
                                            key={ridx}
                                            href={url}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="weekly-resource-link-small"
                                            onClick={(e) => e.stopPropagation()}
                                          >
                                            📄 {resource.description || `Resource ${ridx + 1}`}
                                          </a>
                                        ) : null;
                                      })}
                                    </div>
                                  )}
                                </div>
                              ))}
                              {dayAssignments.map(assignment => (
                                <div key={assignment.id} className="weekly-lesson-item" style={{ borderLeft: '3px solid #f59e0b', backgroundColor: '#fef3c7' }}>
                                  <div className="weekly-lesson-item-title">📝 {assignment.title}</div>
                                  {assignment.classNames && assignment.classNames.length > 0 && (
                                    <div className="weekly-lesson-item-unit">{assignment.classNames.join(', ')}</div>
                                  )}
                                  {assignment.dueDate && (
                                    <div className="weekly-lesson-item-duration" style={{ color: '#b91c1c' }}>
                                      Due: {new Date(assignment.dueDate.toDate ? assignment.dueDate.toDate() : assignment.dueDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                                    </div>
                                  )}
                                </div>
                              ))}
                              {sundayAssignments.map(assignment => (
                                <div key={assignment.id} className="weekly-lesson-item" style={{ borderLeft: '3px solid #f59e0b', backgroundColor: '#fef3c7' }}>
                                  <div className="weekly-lesson-item-title">📝 {assignment.title}</div>
                                  {assignment.classNames && assignment.classNames.length > 0 && (
                                    <div className="weekly-lesson-item-unit">{assignment.classNames.join(', ')}</div>
                                  )}
                                  {assignment.dueDate && (
                                    <div className="weekly-lesson-item-duration" style={{ color: '#b91c1c' }}>
                                      Due: {new Date(assignment.dueDate.toDate ? assignment.dueDate.toDate() : assignment.dueDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                                    </div>
                                  )}
                                </div>
                              ))}
                            </>
                          )
                        ) : (
                          // Weekday view
                          dayLessons.length === 0 && dayAssignments.length === 0 ? (
                            <div className="no-lessons-day">
                              <p>No lessons</p>
                            </div>
                          ) : (
                            <>
                              {dayLessons.map(lesson => (
                                <div key={lesson.id} className="weekly-lesson-item">
                                  <div className="weekly-lesson-item-title">{lesson.title}</div>
                                  {getUnitNames(lesson) && (
                                    <div className="weekly-lesson-item-unit">{getUnitNames(lesson)}</div>
                                  )}
                                  {lesson.duration && (
                                    <div className="weekly-lesson-item-duration">{lesson.duration} min</div>
                                  )}
                                  {lesson.resources && lesson.resources.length > 0 && (
                                    <div className="weekly-lesson-item-resources">
                                      {lesson.resources.map((resource, ridx) => {
                                        const url = resource.fileUrl || resource.url;
                                        return url ? (
                                          <a
                                            key={ridx}
                                            href={url}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="weekly-resource-link-small"
                                            onClick={(e) => e.stopPropagation()}
                                          >
                                            📄 {resource.description || `Resource ${ridx + 1}`}
                                          </a>
                                        ) : null;
                                      })}
                                    </div>
                                  )}
                                </div>
                              ))}
                              {dayAssignments.map(assignment => (
                                <div key={assignment.id} className="weekly-lesson-item" style={{ borderLeft: '3px solid #f59e0b', backgroundColor: '#fef3c7' }}>
                                  <div className="weekly-lesson-item-title">📝 {assignment.title}</div>
                                  {assignment.classNames && assignment.classNames.length > 0 && (
                                    <div className="weekly-lesson-item-unit">{assignment.classNames.join(', ')}</div>
                                  )}
                                  {assignment.dueDate && (
                                    <div className="weekly-lesson-item-duration" style={{ color: '#b91c1c' }}>
                                      Due: {new Date(assignment.dueDate.toDate ? assignment.dueDate.toDate() : assignment.dueDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                                    </div>
                                  )}
                                </div>
                              ))}
                            </>
                          )
                        )}
                      </div>
                    </div>
                  );
                });
              })()}
            </div>
          </div>
        </div>
      ) : (
        <div className="no-class-section">
          <div className="no-class-message">
            <h2>No Class in Session</h2>
            <p>Enjoy your break! Next class period will appear here automatically.</p>
          </div>
        </div>
      )}
    </div>
  );
}

export default DailySummary;
